<app-main-layout>
  <div class="bg-gray-50 min-h-screen">
  <div class="py-6 px-4 sm:px-6 max-w-[1400px] mx-auto">

    <!-- ============ HERO ============ -->
    <div class="relative overflow-hidden rounded-3xl bg-neutral-900 text-white px-6 sm:px-8 py-7 mb-6 shadow-lg">
      <div class="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full bg-amber-400/20 blur-3xl"></div>
      <div class="pointer-events-none absolute right-10 bottom-0 opacity-[0.07]"><mat-icon class="!text-[160px] !h-[160px] !w-[160px]">location_on</mat-icon></div>

      <div class="relative flex flex-col lg:flex-row lg:items-end justify-between gap-6">
        <div class="max-w-xl">
          <div class="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.15em] text-amber-400">
            <mat-icon class="!text-[14px] !h-3.5 !w-3.5">admin_panel_settings</mat-icon> Admin
          </div>
          <h1 class="mt-2 text-3xl font-bold tracking-tight">Postcodes</h1>
          <p class="mt-2 text-sm text-white/60 leading-relaxed">Our own address book - every address customers pick with "Find address" lands here. Search, correct, add or remove entries.</p>
        </div>
        <button type="button"
          class="group self-start lg:self-auto flex items-center gap-2.5 pl-2.5 pr-5 py-2.5 rounded-2xl bg-amber-400 text-neutral-900 font-bold text-sm shadow-lg shadow-amber-500/20 hover:bg-amber-300 hover:-translate-y-0.5 active:translate-y-0 transition-all focus:outline-none focus:ring-4 focus:ring-amber-400/40"
          (click)="openAdd()">
          <span class="h-8 w-8 rounded-xl bg-neutral-900 text-amber-400 flex items-center justify-center transition-transform duration-200 group-hover:rotate-90">
            <mat-icon class="!text-[20px] !h-5 !w-5">add</mat-icon>
          </span>
          New address
        </button>
      </div>

      <div class="relative mt-7 grid grid-cols-2 sm:grid-cols-4 gap-3">
        @for (s of [
          { label: 'Addresses', icon: 'home_pin', n: stats?.addresses },
          { label: 'Postcodes', icon: 'markunread_mailbox', n: stats?.postcodes },
          { label: 'Counties', icon: 'map', n: stats?.counties },
          { label: search ? 'Matching' : 'Showing', icon: 'filter_list', n: loading ? undefined : total }
        ]; track s.label) {
        <div class="rounded-2xl bg-white/[0.06] border border-white/10 px-4 py-3">
          <div class="flex items-center gap-1.5 text-[11px] font-semibold text-white/50 uppercase tracking-wide"><mat-icon class="!text-[14px] !h-3.5 !w-3.5">{{ s.icon }}</mat-icon>{{ s.label }}</div>
          <div class="mt-1 text-2xl font-bold tabular-nums">{{ s.n === undefined || s.n === null ? '–' : (s.n | number) }}</div>
        </div>
        }
      </div>
    </div>

    <!-- ============ TOOLBAR ============ -->
    <div class="sticky top-0 z-20 -mx-4 sm:mx-0 px-4 sm:px-0 py-3 mb-4 bg-gray-50/90 backdrop-blur">
      <div class="relative">
        <mat-icon class="absolute left-3.5 top-1/2 -translate-y-1/2 !text-gray-400 !text-[20px] !h-5 !w-5 pointer-events-none">search</mat-icon>
        <input type="text" placeholder="Search by postcode, street, town or county…"
          class="w-full pl-11 pr-10 py-2.5 rounded-xl border border-gray-200 bg-white shadow-sm focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none transition-all text-sm"
          [ngModel]="search" (ngModelChange)="onSearch($event)" />
        @if (search) {
        <button class="absolute right-2.5 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700 flex items-center justify-center" (click)="onSearch('')" aria-label="Clear search">
          <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">close</mat-icon>
        </button>
        }
      </div>
    </div>

    @if (loadError) {
      <div class="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-2xl p-4 text-sm flex items-center justify-between gap-3">
        <span class="flex items-center gap-2"><mat-icon class="!text-[18px] !h-[18px] !w-[18px] flex-shrink-0">cloud_off</mat-icon>{{ loadError }}</span>
        <button class="text-xs uppercase font-semibold underline flex-shrink-0" (click)="load()">Try again</button>
      </div>
    }

    <!-- ============ LIST ============ -->
    @if (loading) {
      <div class="flex flex-col gap-3">
        @for (i of [1, 2, 3, 4, 5]; track i) {
        <div class="flex items-center gap-4 rounded-2xl bg-white border border-gray-100 px-5 py-4 animate-pulse">
          <div class="h-12 w-12 rounded-xl bg-gray-100"></div>
          <div class="flex-1"><div class="h-4 w-1/4 rounded bg-gray-100"></div><div class="mt-2 h-3 w-1/3 rounded bg-gray-100"></div></div>
          <div class="hidden md:block h-6 w-24 rounded-full bg-gray-100"></div>
        </div>
        }
      </div>
    } @else if (!loadError && rows.length === 0) {
      <div class="rounded-3xl border-2 border-dashed border-gray-200 bg-white py-16 text-center">
        <span class="mx-auto h-14 w-14 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center"><mat-icon class="!text-[28px] !h-7 !w-7">{{ search ? 'search_off' : 'location_off' }}</mat-icon></span>
        <p class="mt-3 text-sm font-semibold text-gray-700">{{ search ? 'No addresses match' : 'No addresses yet' }}</p>
        <p class="text-xs text-gray-400">{{ search ? 'Try a different postcode or street' : 'They appear here as customers use "Find address"' }}</p>
        @if (search) {
          <button type="button" class="mt-4 text-xs font-semibold text-amber-700 hover:underline" (click)="onSearch('')">Clear search</button>
        }
      </div>
    } @else if (rows.length) {
      <div class="rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden">
        @for (r of rows; track r.id; let i = $index) {
        <div role="button" tabindex="0"
          class="group flex items-center gap-3 px-4 sm:px-5 py-3 cursor-pointer transition-colors focus:outline-none focus-visible:bg-amber-50"
          [ngClass]="[editing?.id === r.id ? 'bg-amber-50/80 shadow-[inset_3px_0_0_#f59e0b]' : 'hover:bg-gray-50/80', i > 0 ? 'border-t border-gray-100' : '']"
          (click)="openEdit(r)" (keydown.enter)="openEdit(r)">

          <!-- Icon -->
          <span class="h-11 w-11 rounded-xl bg-gradient-to-br from-amber-300 to-amber-500 text-neutral-900 shadow-sm shadow-amber-200 flex items-center justify-center flex-shrink-0">
            <mat-icon class="!text-[20px] !h-5 !w-5">home_pin</mat-icon>
          </span>

          <!-- Address -->
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-2 min-w-0">
              <span class="font-semibold text-gray-900 truncate text-[15px]">{{ r.address1 }}</span>
            </div>
            <div class="text-[12px] text-gray-500 truncate">{{ r.address2 ? r.address2 + ' · ' : '' }}{{ r.city }}</div>
            <!-- Phones: postcode sits under the address so the name has room -->
            <span class="sm:hidden inline-block mt-1 text-[11px] font-bold px-2 py-0.5 rounded-md bg-neutral-900 text-amber-400 tabular-nums tracking-wide">{{ r.postalcode }}</span>
          </div>

          <!-- Postcode & county -->
          <div class="hidden md:flex items-center justify-end gap-2 flex-shrink-0">
            <span class="w-[140px] flex justify-end">
              @if (r.county) {
                <span class="flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-lg bg-sky-50 text-sky-700 truncate max-w-full"><mat-icon class="!text-[13px] !h-[13px] !w-[13px]">map</mat-icon>{{ r.county }}</span>
              }
            </span>
          </div>
          <span class="hidden sm:inline-block flex-shrink-0 text-xs font-bold px-2.5 py-1 rounded-lg bg-neutral-900 text-amber-400 tabular-nums tracking-wide">{{ r.postalcode }}</span>

          <!-- Actions -->
          <div class="flex items-center gap-0.5 rounded-xl bg-gray-50 border border-gray-100 p-0.5 flex-shrink-0 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100 transition-opacity"
            [class.md:opacity-100]="editing?.id === r.id">
            <button class="h-8 w-8 rounded-lg text-gray-500 hover:bg-white hover:text-gray-900 hover:shadow-sm flex items-center justify-center" (click)="$event.stopPropagation(); openEdit(r)" matTooltip="Edit" aria-label="Edit">
              <mat-icon class="!text-[17px] !h-[17px] !w-[17px]">edit</mat-icon>
            </button>
            <button class="h-8 w-8 rounded-lg text-gray-500 hover:bg-red-50 hover:text-red-600 flex items-center justify-center" (click)="$event.stopPropagation(); askDelete(r)" matTooltip="Delete" aria-label="Delete">
              <mat-icon class="!text-[17px] !h-[17px] !w-[17px]">delete</mat-icon>
            </button>
          </div>
        </div>
        }
      </div>

      <div class="mt-4 rounded-2xl bg-white border border-gray-100 shadow-sm overflow-hidden">
        <mat-paginator
          [length]="total" [pageSize]="pageSize" [pageIndex]="pageIndex"
          [pageSizeOptions]="[20, 50, 100]" showFirstLastButtons (page)="onPage($event)">
        </mat-paginator>
      </div>
    }
  </div>
  </div>

  <!-- ============ ADD / EDIT (slide-over) ============ -->
  @if (editing) {
  <div class="fixed inset-0 z-40 bg-black/30 backdrop-blur-[1px] pc-fade" (click)="closeForm()"></div>
  <aside class="fixed inset-y-0 right-0 z-50 w-full max-w-lg bg-white shadow-2xl flex flex-col pc-slide" role="dialog" aria-modal="true" [attr.aria-label]="editing.id ? 'Edit address' : 'New address'">
    <form class="flex-1 flex flex-col min-h-0" (ngSubmit)="save()">
      <!-- Hero -->
      <div class="relative h-40 bg-gradient-to-br from-neutral-900 to-neutral-700 overflow-hidden flex-shrink-0">
        <div class="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full bg-amber-400/20 blur-2xl"></div>
        <div class="pointer-events-none absolute right-6 bottom-2 opacity-10"><mat-icon class="!text-[96px] !h-24 !w-24 text-white">home_pin</mat-icon></div>
        <button type="button" class="absolute top-4 right-4 h-9 w-9 rounded-full bg-black/40 text-white hover:bg-black/60 flex items-center justify-center backdrop-blur" (click)="closeForm()" aria-label="Close"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">close</mat-icon></button>
        <div class="absolute bottom-4 left-6 right-6">
          <div class="text-[11px] font-semibold uppercase tracking-[0.12em] text-amber-300">{{ editing.id ? 'Edit address' : 'New address' }}</div>
          <div class="mt-1 text-2xl font-bold text-white leading-tight truncate">{{ editing.address1 || 'Address' }}</div>
          <div class="text-xs text-white/60 font-mono">{{ editing.postalcode || 'Postcode' }}</div>
        </div>
      </div>

      <div class="flex-1 overflow-y-auto p-6 flex flex-col gap-5">
        <div>
          <div class="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">Address</div>
          <div class="flex flex-col gap-3">
            <label class="block">
              <span class="text-xs font-semibold text-gray-600">Address line 1 *</span>
              <input name="address1" [(ngModel)]="editing.address1" required maxlength="255" class="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none" />
            </label>
            <label class="block">
              <span class="text-xs font-semibold text-gray-600">Address line 2</span>
              <input name="address2" [(ngModel)]="editing.address2" maxlength="255" class="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none" />
            </label>
          </div>
        </div>
        <div>
          <div class="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-2">Location</div>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label class="block">
              <span class="text-xs font-semibold text-gray-600">City</span>
              <input name="city" [(ngModel)]="editing.city" maxlength="100" class="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none" />
            </label>
            <label class="block">
              <span class="text-xs font-semibold text-gray-600">County</span>
              <input name="county" [(ngModel)]="editing.county" maxlength="100" class="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none" />
            </label>
            <label class="block">
              <span class="text-xs font-semibold text-gray-600">Postcode *</span>
              <input name="postalcode" [(ngModel)]="editing.postalcode" required maxlength="10" class="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm font-semibold uppercase focus:border-amber-400 focus:ring-4 focus:ring-amber-100 outline-none" />
            </label>
          </div>
          <p class="mt-2 text-xs text-gray-400">The postcode is tidied to "CO6 1EP" format when saved.</p>
        </div>
        @if (formError) {
          <div class="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-3 py-2.5"><mat-icon class="!text-[18px] !h-[18px] !w-[18px]">error</mat-icon>{{ formError }}</div>
        }
        @if (editing.id) {
          <div class="text-xs text-gray-400">ID <span class="font-mono text-gray-600">{{ editing.id }}</span></div>
        }
      </div>

      <!-- Buttons -->
      <div class="flex items-center gap-2 px-6 py-4 border-t border-gray-100 flex-shrink-0">
        <button type="submit" [disabled]="saving"
          class="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-b from-amber-400 to-amber-500 text-neutral-900 font-bold text-sm shadow-sm hover:from-amber-300 hover:to-amber-400 transition-all disabled:opacity-60">
          @if (saving) { <mat-spinner diameter="16"></mat-spinner> } @else { <mat-icon class="!text-[18px] !h-[18px] !w-[18px]">{{ editing.id ? 'save' : 'add' }}</mat-icon> }
          {{ editing.id ? 'Save changes' : 'Add address' }}
        </button>
        <button type="button" (click)="closeForm()" [disabled]="saving" class="px-3.5 py-2 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors">Cancel</button>
        @if (editing.id) {
          <button type="button" class="ml-auto h-9 w-9 rounded-xl border border-red-100 text-red-500 hover:bg-red-50 flex items-center justify-center transition-colors" (click)="askDelete(editing)" matTooltip="Delete address" aria-label="Delete address">
            <mat-icon class="!text-[20px] !h-5 !w-5">delete</mat-icon>
          </button>
        }
      </div>
    </form>
  </aside>
  }

  <!-- ============ DELETE CONFIRM ============ -->
  @if (deleting) {
  <div class="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-[1px] p-4 pc-fade" (click)="!removing && (deleting = null)">
    <div class="w-full max-w-sm rounded-3xl bg-white p-6 shadow-2xl" (click)="$event.stopPropagation()" role="alertdialog" aria-modal="true">
      <span class="h-11 w-11 rounded-2xl bg-red-50 text-red-600 flex items-center justify-center"><mat-icon>delete</mat-icon></span>
      <h2 class="mt-3 text-lg font-bold text-gray-900">Delete this address?</h2>
      <p class="mt-1 text-sm text-gray-600">{{ deleting.address1 }}@if (deleting.address2) {, {{ deleting.address2 }}}, <span class="font-semibold">{{ deleting.postalcode }}</span></p>
      <div class="mt-5 flex justify-end gap-2">
        <button type="button" (click)="deleting = null" [disabled]="removing" class="px-3.5 py-2 rounded-xl bg-white border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50">Cancel</button>
        <button type="button" (click)="confirmDelete()" [disabled]="removing" class="flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-60">
          @if (removing) { <mat-spinner diameter="16" color="accent"></mat-spinner> }
          Delete
        </button>
      </div>
    </div>
  </div>
  }
</app-main-layout>
